import React, { useState, useEffect, useRef } from 'react';
import FormulaireMemo from '../components/FormulaireMemo';
import { useNavigate, useParams } from 'react-router-dom';
import useAuthStore from '../store/authStore';
import { useDashboard } from '../store/useDashboard';
import api from '../services/api';
import useEstMobile from '../hooks/useEstMobile';
import {
  IconeCrayon, IconeHorloge, IconePlume, IconeEnvoi, IconeCouronne, IconeValide,
  IconeRafraichir, IconeRefuse, IconeListe, IconeNote, IconeAvion, IconeRecu,
  IconeDocument, IconeFeu, IconeEpingle, IconeCompte, IconePanier, IconeGraphique,
  IconeEclair, IconeCadenas, IconeTrombone, IconeCorbeille, IconeAlerte,
  IconeMonnaie,
} from '../components/IconesSVG';
import { LABELS_ROLES } from '../constants/roles';

import { badgeSeuil } from '../constants/seuilPdg';
const STATUTS = {
  brouillon:                  { label: 'Brouillon',        bg: '#F2F3F4', color: '#555',    icon: IconeCrayon },
  en_controle_terrain:        { label: 'En cours',         bg: '#FEF9E7', color: '#9A7D0A', icon: IconeHorloge },
  en_memo_filiale:            { label: 'Chez Comptable',   bg: '#FEF9E7', color: '#9A7D0A', icon: IconeHorloge },
  en_signature_dg_filiale:    { label: 'Chez DG Filiale',  bg: '#EAF4FB', color: '#1A5E8A', icon: IconePlume },
  envoye_holding:             { label: 'Chez Holding',     bg: '#EAF4FB', color: '#1A5E8A', icon: IconeEnvoi },
  dans_parapheur_pdg:         { label: 'Chez PDG',         bg: '#F5EEF8', color: '#6C3483', icon: IconeCouronne },
  solde:                      { label: 'Approuvé',         bg: '#EAFAF1', color: '#1E8449', icon: IconeValide },
  
  en_correction:               { label: 'A corriger',           color: '#C0392B', bg: '#FDEDEC', icon: IconeRafraichir },
  en_correction_memo:          { label: 'Memo a corriger',      color: '#C0392B', bg: '#FDEDEC', icon: IconeRafraichir },
  en_correction_holding:       { label: 'Holding a corriger',   color: '#C0392B', bg: '#FDEDEC', icon: IconeRafraichir },
  en_correction_pdg:           { label: 'PDG a corriger',       color: '#C0392B', bg: '#FDEDEC', icon: IconeRafraichir },
  rejete:                     { label: 'Rejeté',           bg: '#FDEDEC', color: '#96281B', icon: IconeRefuse },
};

const TYPES = {
  expression_besoin: { label: 'Fiche Expression de Besoin', icon: IconeListe, color: '#2980B9' },
  bon_commande:      { label: 'Bon de Commande',            icon: IconeNote, color: '#8E44AD' },
  frais_mission:     { label: 'Note de Frais de Mission',   icon: IconeAvion, color: '#E67E22' },
  facture:           { label: 'Facture Fournisseur',        icon: IconeRecu, color: '#16A085' },
  regularisation:    { label: 'Régularisation',             icon: IconeRafraichir, color: '#935116' },
};

const PRIORITES = {
  P1: { label: 'P1 — Urgent', color: '#C0392B', bg: '#FDEDEC', icon: IconeFeu },
  P2: { label: 'P2 — Normal', color: '#9A7D0A', bg: '#FEF9E7', icon: IconeEpingle },
  P3: { label: 'P3 — Faible', color: '#2980B9', bg: '#EAF4FB', icon: IconeHorloge },
};

const CIRCUIT = [
  { etape: 'controle_terrain', label: 'Validation',   icon: IconeCompte },
  { etape: 'memo_filiale',     label: 'Comptable',    icon: IconePanier },
  { etape: 'signature_dg',     label: 'Mémo Filiale', icon: IconeCrayon },
  { etape: 'holding',          label: 'DG Filiale',   icon: IconePlume },
  { etape: 'parapheur_pdg',    label: 'Mémo Holding', icon: IconeDocument },
  { etape: 'archive',          label: 'PDG',          icon: IconeCouronne },
];

// Config actions par rôle
// Statuts de correction par rôle
const STATUTS_CORRECTION = {
  demandeur:                ['en_correction'],
  chef_projet:              ['en_correction'],
  acheteur:                 ['en_controle_terrain'],
  responsable_service:      ['en_controle_terrain'],
  direction_achats:         ['en_memo_filiale'],
  preparateur_memo_filiale: ['en_memo_filiale', 'en_correction_memo'],
  dg_filiale:               ['en_signature_dg_filiale', 'en_correction_holding'],
  preparateur_memo_holding: ['envoye_holding', 'en_correction_pdg'],
};

const CONFIG_ACTIONS = {
  acheteur:                 { statut: 'en_controle_terrain',     action: 'approuver',        upload: null,           label: 'Approuver et transmettre' },
  responsable_service:      { statut: 'en_controle_terrain',     action: 'approuver',        upload: null,           label: 'Approuver et transmettre' },
  direction_achats:         { statut: 'en_memo_filiale',         action: 'approuver',        upload: null,           label: 'Approuver et transmettre' },
  preparateur_memo_filiale: { statut: 'en_memo_filiale',         action: 'upload_puis_transmettre', upload: 'memo_filiale',  label: 'Transmettre au DG' },
  dg_filiale:               { statut: 'en_signature_dg_filiale', action: 'signer',           upload: null,           label: 'Signer le Mémo Filiale' },
  preparateur_memo_holding: { statut: 'envoye_holding',          action: 'upload_puis_transmettre', upload: 'memo_holding', label: 'Transmettre au PDG' },
};

const formatDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
};
const formatDateHeure = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};
const formatHeure = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
};
const formatMontant = (m) => m ? Number(m).toLocaleString('fr-FR') + ' FCFA' : '—';
const formatSize = (bytes) => {
  if (!bytes) return '—';
  if (bytes < 1024) return bytes + ' o';
  if (bytes < 1048576) return Math.round(bytes / 1024) + ' Ko';
  return (bytes / 1048576).toFixed(1) + ' Mo';
};

const getToken = () => {
  try {
    const keys = Object.keys(localStorage);
    for (const key of keys) {
      const val = localStorage.getItem(key);
      if (val?.startsWith('eyJ')) return val;
      try { const p = JSON.parse(val); return p?.state?.token || p?.token; } catch { }
    }
  } catch { }
  return null;
};

export default function DetailDemandePage() {
  const estMobile = useEstMobile();
  const { id } = useParams();
  const navigate = useNavigate();
  const { allerDashboard } = useDashboard();
  const { user } = useAuthStore();

  const [demande, setDemande] = useState(null);
  const [showModifMontant, setShowModifMontant] = React.useState(false);
  const [nouveauMontant, setNouveauMontant] = React.useState('');
  const [motifModif, setMotifModif] = React.useState('');
  const [savingMontant, setSavingMontant] = React.useState(false);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [loadingPDF, setLoadingPDF] = useState(false);
  const [hashResultats, setHashResultats] = useState({});
  const [verificationHash, setVerificationHash] = useState(false);

  // Actions
  const [actionMode, setActionMode] = useState(null);
  const [commentaire, setCommentaire] = useState('');
  const [saving, setSaving] = useState(false);
  const [actionSuccess, setActionSuccess] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState('');
  const [uploadingDevis, setUploadingDevis] = React.useState(false);
  const inputDevisRef = React.useRef(null);

  const [actionError, setActionError] = useState('');
  const [memoUploade, setMemoUploade] = useState(false);

  const fileInputRef = useRef(null);
  const [showFormulaireMemo, setShowFormulaireMemo] = useState(false);
  const [donneesMemo, setDonneesMemo] = useState(null);

  const roleUser = user?.roles?.[0]?.role || user?.role;
  const configAction = CONFIG_ACTIONS[roleUser];

  // eslint-disable-next-line react-hooks/exhaustive-deps -- rechargement volontairement limite au changement d'id
  useEffect(() => { charger(); }, [id]);

  // Ecouteur focus retire : provoquait un remontage de la page pendant
  // la fermeture du selecteur de fichier, ce qui annulait les uploads.


  // Le montant n'est modifiable que par le role dont c'est le tour MAINTENANT :
  // a chaque role ses statuts d'action. Croiser une liste de roles et une liste de
  // statuts (ancienne regle) laissait un acheteur modifier le montant longtemps
  // apres avoir transmis le dossier.
  // Doit rester identique a STATUTS_MONTANT_PAR_ROLE dans backend/src/routes/pg/pochettes.routes.js (PATCH /:id/montant)
  const STATUTS_MONTANT_PAR_ROLE = {
    demandeur:                ['en_correction'],
    acheteur:                 ['en_controle_terrain'],
    responsable:              ['en_attente_responsable'],
    preparateur_memo_filiale: ['en_memo_filiale', 'en_correction_memo'],
    preparateur_memo_holding: ['envoye_holding'],
  };
  const peutModifierMontant = !!demande && (STATUTS_MONTANT_PAR_ROLE[roleUser] || []).includes(demande.statut);

  const modifierMontant = async () => {
    if (!nouveauMontant || isNaN(Number(nouveauMontant))) { alert('Montant invalide'); return; }
    if (Number(nouveauMontant) < 0) { alert('Le montant ne peut pas être négatif'); return; }
    setSavingMontant(true);
    try {
      await api.patch('/demandes/' + demande.id + '/montant', {
        montant: Number(nouveauMontant),
        motif: motifModif || 'Modification du montant',
      });
      await charger();
      setShowModifMontant(false);
      setNouveauMontant('');
      setMotifModif('');
    } catch(e) {
      alert('Erreur: ' + (e.response?.data?.message || e.message));
    } finally {
      setSavingMontant(false);
    }
  };

  const charger = async () => {
    try {
      setLoading(true);
      const [res, auditRes] = await Promise.all([
        api.get('/demandes/' + id),
        api.get('/demandes/' + id + '/audit', { headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }, params: { t: Date.now() } }).catch((err) => { console.error('AUDIT ERROR:', err.response?.status, err.message); return { data: { data: [] } }; }),
      ]);
      const data = res.data?.data || res.data;
      setDemande(data);
      const auditData = Array.isArray(auditRes.data) ? auditRes.data : (auditRes.data?.data || []);
      setAudit(auditData);

      // Vérifier si mémo déjà uploadé
      if (data) {
        const docs = Array.isArray(data.documents) ? data.documents : [];
        const categorieUpload = CONFIG_ACTIONS[roleUser]?.upload;
        if (categorieUpload) {
          setMemoUploade(docs.some(d => d.categorie === categorieUpload));
        }
      }
    } catch (err) {
      setError('Demande introuvable');
    } finally { setLoading(false); }
  };

  const peutAgir = () => {
    if (!demande) return false;
    const statutsRole = STATUTS_CORRECTION[roleUser] || [];
    if (statutsRole.includes(demande.statut)) return true;
    if (configAction) return configAction.statut === demande.statut;
    return false;
  };

  const verifierHash = async () => {
    setVerificationHash(true);
    try {
      const res = await api.get('/demandes/' + id + '/verifier-hash');
      const map = {};
      (res.data?.data || []).forEach(r => { map[r.id] = r; });
      setHashResultats(map);
    } catch(err) { console.error(err); }
    finally { setVerificationHash(false); }
  };

  const voirPDF = async (doc) => {
    if (pdfDoc?.id === doc.id) { setPdfUrl(null); setPdfDoc(null); return; }
    setLoadingPDF(true);
    setPdfDoc(doc);
    try {
      const token = getToken();
      const response = await fetch((process.env.REACT_APP_STATIC_URL || 'http://localhost:5001') + '/' + doc.chemin, {
        headers: token ? { 'Authorization': 'Bearer ' + token } : {}
      });
      const blob = await response.blob();
      setPdfUrl(URL.createObjectURL(blob));
    } catch { setPdfUrl(null); }
    finally { setLoadingPDF(false); }
  };

  const approuver = async () => {
    setSaving(true); setActionError('');
    try {
      await api.post('/demandes/' + demande.id + '/visa', {
        type_visa: 'approbation',
        commentaire: commentaire || 'Approuve',
      });
      setActionSuccess('Dossier approuvé et transmis !');
      setActionMode(null);
      charger();
    } catch (err) {
      if (err.response?.status === 409) {
        setActionError((err.response?.data?.message || 'Dossier déjà traité.'));
        charger();
      } else {
        setActionError(err.response?.data?.message || err.message);
      }
    }
    finally { setSaving(false); }
  };

  const rejeter = async () => {
    if (!commentaire.trim()) { setActionError('Motif obligatoire'); return; }
    setSaving(true); setActionError('');
    try {
      await api.post('/demandes/' + demande.id + '/visa', {
        type_visa: 'rejet',
        commentaire,
      });
      setActionSuccess('Dossier rejeté');
      setActionMode(null);
      charger();
    } catch (err) {
      if (err.response?.status === 409) {
        setActionError((err.response?.data?.message || 'Dossier déjà traité.'));
        charger();
      } else {
        setActionError(err.response?.data?.message || err.message);
      }
    }
    finally { setSaving(false); }
  };


  // Statuts et roles autorises a joindre un devis
  const STATUTS_DEVIS = ["en_attente_responsable","en_controle_terrain","en_memo_filiale","en_signature_dg_filiale","en_correction","en_correction_memo"];
  const ROLES_DEVIS = ["demandeur","acheteur","responsable","preparateur_memo_filiale"];
  const peutJoindreDevis = demande && STATUTS_DEVIS.includes(demande.statut) && ROLES_DEVIS.includes(roleUser);


  const supprimerDocument = async (docId, nomDoc) => {
    if (!window.confirm('Supprimer le fichier "' + nomDoc + '" ?')) return;
    try {
      await api.delete('/demandes/' + demande.id + '/documents/' + docId);
      await charger();
    } catch(e) {
      alert('Erreur suppression: ' + (e.response?.data?.message || e.message));
    }
  };

  const joindreDevis = async (file) => {
    if (!file) return;
    setUploadingDevis(true);
    try {
      const formData = new FormData();
      formData.append('document', file);
      formData.append('categorie', 'devis');
      formData.append('description', 'Devis - ' + file.name);
      await api.post('/demandes/' + demande.id + '/documents', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      await charger();
      setUploadSuccess('Devis joint avec succès');
      setTimeout(() => setUploadSuccess(''), 3000);
    } catch(e) {
      alert('Erreur upload devis: ' + (e.response?.data?.message || e.message));
    } finally {
      setUploadingDevis(false);
    }
  };

  const uploaderMemo = async (file) => {
    setSaving(true); setActionError('');
    try {
      const formData = new FormData();
      formData.append('document', file);
      formData.append('categorie', configAction.upload);
      formData.append('description', 'Memo ' + configAction.upload);
      // Remplacer automatiquement l'ancien mémo s'il existe
      const documents = Array.isArray(demande.documents) ? demande.documents : [];
      const ancienMemo = documents.find(d => d.categorie === configAction.upload);
      if (ancienMemo) formData.append('remplacer_id', ancienMemo.id);
      await api.post('/demandes/' + demande.id + '/documents', formData);
      setMemoUploade(true);
      setUploadSuccess('Mémo uploadé avec succès — vous pouvez transmettre');
      charger();
    } catch (err) {
      if (err.response?.status === 409) {
        setActionError((err.response?.data?.message || 'Dossier déjà traité.'));
        charger();
      } else {
        setActionError(err.response?.data?.message || err.message);
      }
    }
    finally { setSaving(false); }
  };

  const transmettreApresUpload = async () => {
    setSaving(true); setActionError('');
    try {
      await api.post('/demandes/' + demande.id + '/visa', {
        type_visa: 'approbation',
        commentaire: commentaire || 'Memo uploade et transmis',
      });
      setUploadSuccess('');
      setActionSuccess('Dossier transmis à l\'étape suivante !');
      setActionMode(null);
      charger();
    } catch (err) {
      if (err.response?.status === 409) {
        setActionError((err.response?.data?.message || 'Dossier déjà traité.'));
        charger();
      } else {
        setActionError(err.response?.data?.message || err.message);
      }
    }
    finally { setSaving(false); }
  };

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', fontFamily: 'Inter, sans-serif' }}>
      <div style={{ textAlign: 'center', color: '#888' }}>
        <div style={{ marginBottom: 12, color: '#bbb' }}><IconeHorloge taille={40} /></div>
        Chargement...
      </div>
    </div>
  );

  if (error || !demande) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', fontFamily: 'Inter, sans-serif' }}>
      <div style={{ textAlign: 'center', color: '#888' }}>
        <div style={{ marginBottom: 12, color: '#96281B' }}><IconeRefuse taille={40} /></div>
        {error || 'Demande introuvable'}
        <br />
        <button type="button" onClick={allerDashboard} style={{ marginTop: 16, padding: '10px 20px', borderRadius: 8, border: 'none', background: '#C0392B', color: '#fff', cursor: 'pointer', fontFamily: 'inherit' }}>
          Retour au dashboard
        </button>
      </div>
    </div>
  );

  const dernierRejet = audit.filter(a =>
    a.action === 'VISA_APPOSE' && a.details?.type_visa === 'rejet'
  ).pop();

  // Signature du DG filiale : sert a reperer les modifications de montant
  // intervenues APRES la signature du memo filiale (audit trie par timestamp ASC)
  const signatureDG = audit.filter(a =>
    a.action === 'VISA_APPOSE' && a.details?.type_visa !== 'rejet' &&
    (a.details?.type_visa === 'signature_dg' || a.role_acteur === 'dg_filiale' || a.acteur?.role === 'dg_filiale')
  ).pop();
  const dateSignatureDG = signatureDG ? new Date(signatureDG.timestamp) : null;

  const estEnCorrection = demande.statut?.startsWith('en_correction');
  const statut = STATUTS[demande.statut] || STATUTS.brouillon;
  const type = TYPES[demande.type_document] || { label: demande.type_document, icon: IconeDocument, color: '#666' };
  const priorite = PRIORITES[demande.priorite] || PRIORITES.P2;
  const docs = Array.isArray(demande.documents) ? demande.documents : [];
  const memoDoc = docs.find(d => d.categorie === configAction?.upload);

  return (
    <div style={s.page}>

      {/* HEADER */}
      <div style={s.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <button type="button" onClick={allerDashboard} style={s.backBtn}>← Retour</button>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 20, fontWeight: 800, color: '#0D1B2A' }}>{demande.reference}</span>
              <span style={{ ...s.badge, background: statut.bg, color: statut.color }}>
                <statut.icon taille={13} /> {statut.label}
              </span>
              {demande.priorite && (
                <span style={{ ...s.badge, background: priorite.bg, color: priorite.color }}>
                  <priorite.icon taille={12} /> {priorite.label}
                </span>
              )}
            </div>
            <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
              {demande.filiale?.nom} · {demande.demandeur?.prenom} {demande.demandeur?.nom} · Créé le {formatDate(demande.created_at)}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: '#C0392B' }}>
            {formatMontant(demande.montant || demande.montantEstime)}
          </div>
          {/* Le seuil de la filiale accompagne la demande : sans lui, la pastille
              serait devinee, et donc parfois fausse. */}
          {demande.filiale?.seuil_pdg !== undefined && (() => {
            const b = badgeSeuil(demande.montant || demande.montantEstime, demande.filiale.seuil_pdg);
            return (
              <span style={{
                fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 20,
                color: b.couleur, background: b.fond, whiteSpace: 'nowrap',
              }}>{b.libelle}</span>
            );
          })()}
          {peutModifierMontant && (
            <button onClick={() => { setNouveauMontant(String(demande.montant || demande.montantEstime || '')); setShowModifMontant(true); }}
              style={{ fontSize: 11, padding: '3px 10px', borderRadius: 8, border: '1px solid #C0392B', background: 'transparent', color: '#C0392B', cursor: 'pointer', fontFamily: 'inherit' }}>
              Modifier
            </button>
          )}
        </div>
      </div>

      {/* MODAL MODIFICATION MONTANT */}
      {showModifMontant && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="modale-mobile" style={{ background: '#fff', borderRadius: 14, padding: 28, width: 400, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#0D1B2A', marginBottom: 20 }}>Modifier le montant</div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#444', display: 'block', marginBottom: 6 }}>Nouveau montant (FCFA)</label>
              <input type='number' value={nouveauMontant} onChange={e => setNouveauMontant(e.target.value)}
                style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #E5E7EB', fontSize: 14, boxSizing: 'border-box', fontFamily: 'inherit' }} />
              {nouveauMontant && <div style={{ fontSize: 11, color: '#C0392B', marginTop: 4 }}>= {Number(nouveauMontant).toLocaleString('fr-FR')} FCFA</div>}
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#444', display: 'block', marginBottom: 6 }}>Motif de modification <span style={{ color: '#888', fontWeight: 400 }}>(optionnel)</span></label>
              <textarea value={motifModif} onChange={e => setMotifModif(e.target.value)} rows={3}
                placeholder='Ex: Devis reçu, montant corrigé...'
                style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #E5E7EB', fontSize: 13, boxSizing: 'border-box', fontFamily: 'inherit', resize: 'vertical' }} />
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button type="button" onClick={() => setShowModifMontant(false)}
                style={{ flex: 1, padding: '10px', borderRadius: 8, border: '1px solid #E5E7EB', background: '#fff', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13 }}>
                Annuler
              </button>
              <button type="button" onClick={modifierMontant} disabled={savingMontant}
                style={{ flex: 1, padding: '10px', borderRadius: 8, border: 'none', background: '#C0392B', color: '#fff', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 600 }}>
                {savingMontant ? 'Enregistrement...' : 'Confirmer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CIRCUIT */}
      <div style={s.circuit}>
        <div style={{ fontSize: 10, color: '#888', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>Circuit de validation</div>
        <div style={s.circuitSteps}>
          {CIRCUIT.map((step, i) => {
            const isActuelle = demande.etape_actuelle === step.etape;
            const isPasse = CIRCUIT.findIndex(s => s.etape === demande.etape_actuelle) > i;
            const isSolde = demande.statut === 'solde';
            return (
              <React.Fragment key={i}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: '50%',
                    background: isSolde || isPasse ? '#27AE60' : isActuelle ? '#C0392B' : '#F0F0F0',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18,
                    border: isActuelle ? '2px solid #C0392B' : isSolde || isPasse ? '2px solid #27AE60' : '2px solid #E5E7EB',
                    boxShadow: isActuelle ? '0 0 0 3px rgba(192,57,43,0.2)' : 'none',
                  }}>
                    {isSolde || isPasse ? <IconeValide taille={18} /> : <step.icon taille={18} />}
                  </div>
                  <span style={{ fontSize: 10, color: isActuelle ? '#C0392B' : isPasse || isSolde ? '#27AE60' : '#aaa', fontWeight: isActuelle ? 700 : 400 }}>
                    {step.label}
                  </span>
                </div>
                {i < CIRCUIT.length - 1 && (
                  <div style={{ flex: 1, height: 2, background: isSolde || isPasse ? '#27AE60' : '#E5E7EB', marginTop: -14 }} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {estEnCorrection && dernierRejet && (
        <div style={{ background: '#FDEDEC', borderLeft: '4px solid #C0392B', padding: '14px 28px', display: 'flex', alignItems: 'flex-start', gap: 14 }}>
          <div style={{ flexShrink: 0, color: '#96281B' }}><IconeRefuse taille={28} /></div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: '#96281B', marginBottom: 6 }}>
              Dossier rejeté — correction requise
            </div>
            {dernierRejet.details?.commentaire && (
              <div style={{ fontSize: 13, color: '#C0392B', fontWeight: 600, marginBottom: 4, fontStyle: 'italic' }}>
                “{dernierRejet.details.commentaire}”
              </div>
            )}
            <div style={{ fontSize: 12, color: '#888' }}>
              <IconeCompte taille={12} /> {dernierRejet.acteur ? dernierRejet.acteur.prenom + ' ' + dernierRejet.acteur.nom : 'Inconnu'} 
              &nbsp;·&nbsp; 
              <IconeHorloge taille={11} /> {new Date(dernierRejet.timestamp).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
            </div>
          </div>
        </div>
      )}
      <div style={estMobile ? { ...s.body, gridTemplateColumns: '1fr', padding: '14px', gap: 14 } : s.body}>
        {/* COLONNE GAUCHE */}
        <div style={s.leftCol}>

          {/* INFORMATIONS */}
          <div style={s.card}>
            <div style={s.cardTitle}><IconeListe taille={15} /> Informations</div>
            <div style={s.infoGrid}>
              <div style={s.infoItem}>
                <div style={s.infoLabel}>Type de document</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ width: 32, height: 32, borderRadius: 8, background: type.color + '20', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>
                    <type.icon taille={26} />
                  </span>
                  <span style={{ fontSize: 13, fontWeight: 600 }}>{type.label}</span>
                </div>
              </div>
              <div style={s.infoItem}>
                <div style={s.infoLabel}>Filiale</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ background: '#0D1B2A', color: '#fff', padding: '2px 10px', borderRadius: 6, fontSize: 12, fontWeight: 700 }}>
                    {demande.filiale?.code || '—'}
                  </span>
                  <span style={{ fontSize: 13 }}>{demande.filiale?.nom}</span>
                </div>
              </div>
              <div style={s.infoItem}>
                <div style={s.infoLabel}>Demandeur</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 30, height: 30, borderRadius: '50%', background: '#C0392B', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#fff' }}>
                    {demande.demandeur?.prenom?.[0]}{demande.demandeur?.nom?.[0]}
                  </div>
                  <span style={{ fontSize: 13, fontWeight: 600 }}>{demande.demandeur?.prenom} {demande.demandeur?.nom}</span>
                </div>
              </div>
              <div style={s.infoItem}>
                <div style={s.infoLabel}>Montant estimé</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: '#C0392B' }}>
                  {formatMontant(demande.montant || demande.montantEstime)}
                </div>
              </div>
            </div>

            {(demande.titre || demande.motif) && (
              <div style={{ marginTop: 14, padding: '12px 16px', background: '#F9FAFB', borderRadius: 10, border: '0.5px solid #e5e7eb' }}>
                <div style={s.infoLabel}>Objet de la demande</div>
                <div style={{ fontSize: 14, color: '#0D1B2A', fontWeight: 500 }}>{demande.titre || demande.motif}</div>
              </div>
            )}

            {demande.description && (
              <div style={{ marginTop: 10, padding: '12px 16px', background: '#F9FAFB', borderRadius: 10, border: '0.5px solid #e5e7eb' }}>
                <div style={s.infoLabel}>Description</div>
                <div style={{ fontSize: 13, color: '#555' }}>{demande.description}</div>
              </div>
            )}
          </div>

          {/* DATES */}
          <div style={s.card}>
            <div style={s.cardTitle}><IconeHorloge taille={15} /> Dates & Traçabilité</div>
            <div style={s.datesGrid}>
              {[
                { label: 'Créé le', value: formatDateHeure(demande.created_at), icon: IconeHorloge },
                { label: 'Dernière mise à jour', value: formatDateHeure(demande.updated_at), icon: IconeRafraichir },
                { label: 'Statut actuel', value: <><statut.icon taille={13} /> {statut.label}</>, icon: IconeGraphique },
                { label: 'Priorité', value: <><priorite.icon taille={13} /> {priorite.label}</>, icon: IconeEclair },
              ].map((item, i) => (
                <div key={i} style={s.dateItem}>
                  <div style={{ ...s.infoLabel, display: 'flex', alignItems: 'center', gap: 4 }}><item.icon taille={12} /> {item.label}</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#0D1B2A' }}>{item.value}</div>
                </div>
              ))}
            </div>
          </div>

          {/* DOCUMENTS */}
          <div style={s.card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>

              <button type="button" onClick={verifierHash} disabled={verificationHash}
                style={{ padding: '6px 14px', borderRadius: 8, border: '0.5px solid #2980B9', background: '#EAF4FB', color: '#2980B9', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600 }}>
                {verificationHash ? <><IconeHorloge taille={13} /> Vérification...</> : <><IconeCadenas taille={13} /> Vérifier intégrité</>}
              </button>
              {peutJoindreDevis && (
                <div>
                  <input ref={inputDevisRef} type='file' accept='.pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx'
                    style={{ display: 'none' }}
                    onChange={e => e.target.files[0] && joindreDevis(e.target.files[0])} />
                  <button type='button' onClick={() => inputDevisRef.current?.click()} disabled={uploadingDevis}
                    style={{ padding: '6px 14px', borderRadius: 8, border: '0.5px solid #27AE60', background: '#EAFAF1', color: '#1E8449', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600 }}>
                    {uploadingDevis ? <><IconeHorloge taille={13} /> Upload...</> : <><IconeTrombone taille={13} /> Joindre un devis</>}
                  </button>
                </div>
              )}
            </div>
            {docs.length === 0 ? (
              <div style={{ padding: 20, textAlign: 'center', color: '#888', fontSize: 13 }}>Aucun document joint</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {docs.map((doc, i) => (
                  <div key={doc.id || i}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', background: pdfDoc?.id === doc.id ? '#EAF4FB' : '#F9FAFB', borderRadius: 10, border: pdfDoc?.id === doc.id ? '1px solid #2980B9' : '0.5px solid #e5e7eb' }}>
                      <div style={{ width: 40, height: 40, borderRadius: 10, background: doc.categorie === 'memo_filiale' ? '#EAFAF1' : doc.categorie === 'memo_holding' ? '#F5EEF8' : '#EAF4FB', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>
                        {doc.categorie === 'memo_filiale' ? <IconeCrayon taille={18} /> : doc.categorie === 'memo_holding' ? <IconeDocument taille={18} /> : <IconeListe taille={18} />}
                      </div>
                      <div style={{ flex: 1, overflow: 'hidden' }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#0D1B2A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {doc.nom}
                        </div>
                        <div style={{ fontSize: 11, color: '#888', marginTop: 2, display: 'flex', gap: 8 }}>
                          <span style={{ background: doc.categorie === 'memo_filiale' ? '#EAFAF1' : doc.categorie === 'memo_holding' ? '#F5EEF8' : '#EAF4FB', color: doc.categorie === 'memo_filiale' ? '#1E8449' : '#6C3483', padding: '1px 6px', borderRadius: 6, fontSize: 10, fontWeight: 600 }}>
                            {doc.categorie?.replace('_', ' ') || 'document'}
                          </span>
                          <span>{formatSize(doc.taille)}</span>
                          {hashResultats[doc.id] && (
                            <span style={{ 
                              fontSize: 10, fontWeight: 700,
                              color: hashResultats[doc.id].statut === 'valide' ? '#1E8449' : 
                                     hashResultats[doc.id].statut === 'modifie' ? '#96281B' : '#9A7D0A',
                              background: hashResultats[doc.id].statut === 'valide' ? '#EAFAF1' : 
                                          hashResultats[doc.id].statut === 'modifie' ? '#FDEDEC' : '#FEF9E7',
                              padding: '1px 6px', borderRadius: 6
                            }}>
                              {hashResultats[doc.id].statut === 'valide' ? <><IconeValide taille={11} /> Authentique</> : 
                               hashResultats[doc.id].statut === 'modifie' ? <><IconeRefuse taille={11} /> MODIFIÉ !</> : 
                               hashResultats[doc.id].statut === 'fichier_manquant' ? <><IconeAlerte taille={11} /> Manquant</> : '?'}
                            </span>
                          )}
                          {doc.created_at && <span>· {formatHeure(doc.created_at)}</span>}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 6 }}>
                        {doc.mime_type?.includes('pdf') && (
                          <button type="button" onClick={() => voirPDF(doc)}
                            style={{ padding: '6px 12px', borderRadius: 8, border: pdfDoc?.id === doc.id ? '1px solid #2980B9' : '0.5px solid #ddd', background: pdfDoc?.id === doc.id ? '#EAF4FB' : '#fff', color: pdfDoc?.id === doc.id ? '#2980B9' : '#555', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600 }}>
                            {pdfDoc?.id === doc.id ? 'Fermer' : 'Voir'}
                          </button>
                        )}
                      {peutJoindreDevis && doc.categorie !== 'memo_filiale' && doc.categorie !== 'memo_holding' && (
                        <button type='button' onClick={() => supprimerDocument(doc.id, doc.nom)}
                          style={{ padding: '4px 10px', borderRadius: 6, border: '0.5px solid #E74C3C', background: '#FDEDEC', color: '#C0392B', fontSize: 11, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600 }}>
                          <IconeCorbeille taille={13} /> Supprimer
                        </button>
                      )}
                      </div>
                    </div>
                    {pdfDoc?.id === doc.id && (
                      <div style={{ borderRadius: '0 0 10px 10px', overflow: 'hidden', border: '1px solid #2980B9', borderTop: 'none' }}>
                        {loadingPDF ? (
                          <div style={{ padding: 30, textAlign: 'center', color: '#888' }}><IconeHorloge taille={14} /> Chargement...</div>
                        ) : pdfUrl ? (
                          <iframe className="pdf-mobile" src={pdfUrl} style={{ width: '100%', height: 500, border: 'none', display: 'block' }} title="PDF" />
                        ) : null}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* COLONNE DROITE */}
        <div style={s.rightCol}>

          {/* BLOC ACTION */}
          {peutAgir() && (
            <div style={{ ...s.card, border: '1.5px solid #C0392B' }}>
              <div style={s.cardTitle}><IconeEclair taille={15} /> Action requise</div>

              {actionSuccess ? (
                <div style={{ background: '#EAFAF1', border: '1px solid #27AE60', borderRadius: 10, padding: '12px 14px', fontSize: 13, fontWeight: 700, color: '#1E8449' }}>
                  {actionSuccess}
                </div>

              ) : configAction?.action === 'upload_puis_transmettre' ? (
                <div>
                  {/* ÉTAPE 1 — Upload mémo */}
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <div style={{ width: 24, height: 24, borderRadius: '50%', background: memoUploade ? '#27AE60' : '#C0392B', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700 }}>
                        {memoUploade ? <IconeValide taille={13} /> : '1'}
                      </div>
                      <span style={{ fontSize: 13, fontWeight: 700, color: '#0D1B2A' }}>
                        Uploader le {configAction.upload === 'memo_filiale' ? 'Mémo Filiale' : 'Mémo Holding'}
                      </span>
                    </div>

                    {memoUploade ? (
                      <div style={{ background: '#EAFAF1', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#1E8449', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
                        <IconeValide taille={13} /> {memoDoc?.nom || 'Mémo uploadé'}
                        <button type="button" onClick={() => fileInputRef.current?.click()}
                          style={{ marginLeft: 'auto', padding: '3px 8px', borderRadius: 6, border: '0.5px solid #27AE60', background: 'transparent', color: '#27AE60', fontSize: 11, cursor: 'pointer', fontFamily: 'inherit' }}>
                          Remplacer
                        </button>
                      </div>
                    ) : (
                      <div style={{ background: '#FEF9E7', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#9A7D0A', marginBottom: 8 }}>
                        <IconeAlerte taille={13} /> Rédigez votre mémo en Word ou PDF avant de l'uploader ici
                      </div>
                    )}

{uploadSuccess && (
                      <div style={{ background: '#EAFAF1', border: '1px solid #27AE60', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#1E8449', fontWeight: 600, marginBottom: 8 }}>
                        {uploadSuccess}
                      </div>
                    )}
                    {/* Upload manuel masque temporairement
                    <button type="button" onClick={() => fileInputRef.current && fileInputRef.current.click()}
                      style={{ display: 'block', width: '100%', padding: '11px', borderRadius: 10, border: 'none', background: memoUploade ? '#F0F0F0' : 'linear-gradient(135deg, #8E44AD, #6C3483)', color: memoUploade ? '#888' : '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', textAlign: 'center', boxSizing: 'border-box', marginTop: 8 }}>
                      {saving && !memoUploade ? <><IconeHorloge taille={13} /> Upload...</> : memoUploade ? <><IconeTrombone taille={13} /> Changer le mémo</> : <><IconeTrombone taille={13} /> Choisir le PDF mémo</>}
                    </button>
                    */}
                    <input ref={fileInputRef} type="file" accept=".pdf" style={{ display: 'none' }}
                      onChange={e => { if (e.target.files[0]) { uploaderMemo(e.target.files[0]); } e.target.value = ''; }} />

                    <button type="button" onClick={() => setShowFormulaireMemo(true)}
                      style={{ display: 'block', width: '100%', padding: '11px', borderRadius: 10, border: 'none', background: memoUploade ? '#F0F0F0' : 'linear-gradient(135deg, #8E44AD, #6C3483)', color: memoUploade ? '#888' : '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', textAlign: 'center', boxSizing: 'border-box', marginTop: 8 }}>
                      <IconeCrayon taille={13} /> {memoUploade ? 'Changer le mémo' : 'Générer le mémo'}
                    </button>

                    {showFormulaireMemo && (
                      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
                        <div style={{ maxWidth: 480, width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
                          <FormulaireMemo
                            key={'memo-' + demande.id + '-' + showFormulaireMemo}
                            type_memo={configAction.upload === 'memo_filiale' ? 'filiale' : 'holding'}
                            filialeId={demande.filiale_id || demande.filiale?.id}
                            pochetteId={demande.id}
                            demande={demande}
                            valeursInitiales={donneesMemo}
                            onAnnuler={() => setShowFormulaireMemo(false)}
                            onGenere={async (doc, numero, valeursUtilisees) => {
                              setShowFormulaireMemo(false);
                              setMemoUploade(true);
                              setDonneesMemo(valeursUtilisees);
                              setUploadSuccess('Mémo généré avec succès — vous pouvez maintenant transmettre');
                              await charger();
                              if (doc) voirPDF(doc);
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Séparateur */}
                  <div style={{ height: 1, background: '#E5E7EB', marginBottom: 16 }} />

                  {/* ÉTAPE 2 — Transmettre */}
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <div style={{ width: 24, height: 24, borderRadius: '50%', background: memoUploade ? '#C0392B' : '#E5E7EB', color: memoUploade ? '#fff' : '#aaa', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700 }}>
                        2
                      </div>
                      <span style={{ fontSize: 13, fontWeight: 700, color: memoUploade ? '#0D1B2A' : '#aaa' }}>
                        {configAction.label}
                      </span>
                    </div>

                    {!memoUploade && (
                      <div style={{ background: '#F9FAFB', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#aaa', marginBottom: 10 }}>
                        <IconeCadenas taille={13} /> Uploadez d'abord le mémo pour débloquer cette action
                      </div>
                    )}

                    {memoUploade && actionMode !== 'transmettre' && (
                      <textarea style={s.textarea}
                        placeholder="Commentaire optionnel..."
                        value={commentaire}
                        onChange={e => setCommentaire(e.target.value)} />
                    )}

                    <button
                      onClick={transmettreApresUpload}
                      disabled={!memoUploade || saving}
                      style={{
                        width: '100%', padding: '13px', borderRadius: 10, border: 'none',
                        background: memoUploade ? '#27AE60' : '#E5E7EB',
                        color: memoUploade ? '#fff' : '#aaa',
                        fontSize: 13, fontWeight: 700, cursor: memoUploade ? 'pointer' : 'not-allowed',
                        fontFamily: 'inherit', marginBottom: 8,
                        boxShadow: memoUploade ? '0 3px 10px rgba(39,174,96,0.3)' : 'none'
                      }}>
                      {saving ? <><IconeHorloge taille={13} />...</> : <><IconeValide taille={13} /> {configAction.label}</>}
                    </button>
                  </div>

                  {actionError && <div style={s.errorMsg}>{actionError}</div>}

                  {/* Rejeter */}
                  {actionMode !== 'rejeter' ? (
                    <button type="button" onClick={() => setActionMode('rejeter')} style={s.btnRejeter}>
                      <IconeRefuse taille={13} /> Rejeter le dossier
                    </button>
                  ) : (
                    <div>
                      <div style={{ fontSize: 12, color: '#96281B', fontWeight: 700, marginBottom: 8 }}>Motif du rejet (obligatoire)</div>
                      <textarea style={s.textarea}
                        placeholder="Expliquez le motif..."
                        value={commentaire}
                        onChange={e => setCommentaire(e.target.value)} />
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button type="button" onClick={() => { setActionMode(null); setCommentaire(''); }} style={s.btnAnnuler}>Annuler</button>
                        <button type="button" onClick={rejeter} disabled={saving || !commentaire.trim()}
                          style={{ flex: 2, padding: '10px', borderRadius: 8, border: 'none', background: '#C0392B', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                          {saving ? <><IconeHorloge taille={13} />...</> : <><IconeRefuse taille={13} /> Confirmer le rejet</>}
                        </button>
                      </div>
                    </div>
                  )}
                </div>

              ) : configAction?.action === 'approuver' ? (
                <div>
                  {actionMode !== 'approuver' && actionMode !== 'rejeter' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <button type="button" onClick={() => setActionMode('approuver')} style={s.btnApprouver}>
                        <IconeValide taille={13} /> Approuver et transmettre
                      </button>
                      <button type="button" onClick={() => setActionMode('rejeter')} style={s.btnRejeter}>
                        <IconeRefuse taille={13} /> Rejeter
                      </button>
                    </div>
                  )}

                  {actionMode === 'approuver' && (
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#1E8449', marginBottom: 10 }}><IconeValide taille={13} /> Confirmer l'approbation</div>
                      <textarea style={s.textarea} placeholder="Commentaire optionnel..." value={commentaire} onChange={e => setCommentaire(e.target.value)} />
                      {actionError && <div style={s.errorMsg}>{actionError}</div>}
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button type="button" onClick={() => { setActionMode(null); setCommentaire(''); }} style={s.btnAnnuler}>Annuler</button>
                        <button type="button" onClick={approuver} disabled={saving} style={s.btnApprouver}>
                          {saving ? <><IconeHorloge taille={13} />...</> : <><IconeValide taille={13} /> Confirmer</>}
                        </button>
                      </div>
                    </div>
                  )}

                  {actionMode === 'rejeter' && (
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#96281B', marginBottom: 10 }}><IconeRefuse taille={13} /> Motif du rejet</div>
                      <textarea style={s.textarea} placeholder="Motif obligatoire..." value={commentaire} onChange={e => setCommentaire(e.target.value)} />
                      {actionError && <div style={s.errorMsg}>{actionError}</div>}
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button type="button" onClick={() => { setActionMode(null); setCommentaire(''); }} style={s.btnAnnuler}>Annuler</button>
                        <button type="button" onClick={rejeter} disabled={saving} style={{ flex: 2, padding: '10px', borderRadius: 8, border: 'none', background: '#C0392B', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                          {saving ? <><IconeHorloge taille={13} />...</> : <><IconeRefuse taille={13} /> Confirmer</>}
                        </button>
                      </div>
                    </div>
                  )}
                </div>

              ) : configAction?.action === 'signer' ? (
                <div>
                  <div style={{ background: '#EAF4FB', borderRadius: 10, padding: '12px 14px', marginBottom: 14, fontSize: 13, color: '#1A5E8A' }}>
                    <IconePlume taille={13} /> Pour signer ce dossier, utilisez le parapheur pour apposer votre signature directement sur le mémo filiale.
                  </div>
                  <button type="button" onClick={() => navigate('/parapheur-role')} style={s.btnApprouver}>
                    <IconePlume taille={14} /> Ouvrir le parapheur pour signer
                  </button>
                  <button type="button" onClick={() => setActionMode('rejeter')} style={s.btnRejeter}><IconeRefuse taille={13} /> Rejeter</button>
                  {actionMode === 'rejeter' && (
                    <div style={{ marginTop: 10 }}>
                      <textarea style={s.textarea} placeholder="Motif obligatoire..." value={commentaire} onChange={e => setCommentaire(e.target.value)} />
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button type="button" onClick={() => { setActionMode(null); setCommentaire(''); }} style={s.btnAnnuler}>Annuler</button>
                        <button type="button" onClick={rejeter} disabled={saving} style={{ flex: 2, padding: '10px', borderRadius: 8, border: 'none', background: '#C0392B', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                          {saving ? <><IconeHorloge taille={13} />...</> : <><IconeRefuse taille={13} /> Confirmer</>}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          )}

          {/* HISTORIQUE */}
          <div style={s.card}>
            <div style={s.cardTitle}><IconeListe taille={15} /> Historique des actions</div>
            {audit.length === 0 ? (
              <div style={{ padding: '16px 0', textAlign: 'center', color: '#888', fontSize: 13 }}>
                <div style={{ marginBottom: 8, color: '#bbb' }}><IconeHorloge taille={28} /></div>
                En attente de traitement
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {audit.map((a, i) => {
                  // Modification de montant intervenue apres la signature du DG filiale :
                  // le memo filiale signe ne correspond plus au montant du dossier
                  const apresSignatureDG = a.action === 'MODIFICATION_MONTANT' && dateSignatureDG &&
                    new Date(a.timestamp) > dateSignatureDG;
                  const roleActeur = LABELS_ROLES[a.role_acteur || a.acteur?.role] || a.role_acteur || a.acteur?.role;
                  return (
                  <div key={a.id || i} style={{ display: 'flex', gap: 12, paddingBottom: 16, position: 'relative' }}>
                    {i < audit.length - 1 && (
                      <div style={{ position: 'absolute', left: 15, top: 32, bottom: 0, width: 2, background: '#F0F0F0' }} />
                    )}
                    <div style={{ width: 32, height: 32, borderRadius: '50%', background: apresSignatureDG ? '#FDEDEC' : a.action === 'VISA_APPOSE' ? '#EAFAF1' : a.action === 'MODIFICATION_MONTANT' ? '#FEF9E7' : '#EAF4FB', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, flexShrink: 0, border: apresSignatureDG ? '1px solid #E6B0AA' : '0.5px solid #e5e7eb', zIndex: 1 }}>
                      {apresSignatureDG ? <IconeAlerte taille={15} /> : a.action === 'POCHETTE_CREEE' ? <IconeCrayon taille={15} /> : a.action === 'VISA_APPOSE' ? <IconeValide taille={15} /> : a.action === 'MODIFICATION_MONTANT' ? <IconeMonnaie taille={15} /> : <IconeListe taille={15} />}
                    </div>
                    <div style={{ flex: 1 }}>
                      {apresSignatureDG ? (
                        <div style={{ background: '#FDEDEC', border: '1px solid #E6B0AA', borderRadius: 8, padding: '8px 10px' }}>
                          <div style={{ fontSize: 12, fontWeight: 800, color: '#96281B', marginBottom: 4 }}>
                            <IconeAlerte taille={12} /> Montant modifié après signature DG
                          </div>
                          <div style={{ fontSize: 12, fontWeight: 700, color: '#C0392B', marginBottom: 2 }}>
                            {Number(a.details?.ancien_montant || 0).toLocaleString('fr-FR')} → {Number(a.details?.nouveau_montant || 0).toLocaleString('fr-FR')} FCFA
                          </div>
                          {a.details?.motif && (
                            <div style={{ fontSize: 11, color: '#96281B', fontStyle: 'italic', marginBottom: 2 }}>
                              "{a.details.motif}"
                            </div>
                          )}
                          <div style={{ fontSize: 10, color: '#96281B' }}>
                            Par : {a.acteur ? a.acteur.prenom + ' ' + a.acteur.nom + (roleActeur ? ' (' + roleActeur + ')' : '') : (roleActeur || 'Inconnu')}
                            &nbsp;·&nbsp;{formatDateHeure(a.timestamp)}
                          </div>
                        </div>
                      ) : (
                        <>
                          <div style={{ fontSize: 12, fontWeight: 700, color: '#0D1B2A', marginBottom: 2 }}>
                            {a.action === 'POCHETTE_CREEE' ? 'Dossier créé' :
                             a.action === 'VISA_APPOSE' ? 'Visa apposé — ' + (a.details?.type_visa || '') :
                             a.action === 'MODIFICATION_MONTANT' ? <><IconeMonnaie taille={12} /> {'Montant modifié : ' + Number(a.details?.ancien_montant || 0).toLocaleString('fr-FR') + ' → ' + Number(a.details?.nouveau_montant || 0).toLocaleString('fr-FR') + ' FCFA' + (a.details?.motif ? ' — ' + a.details.motif : '')}</> :
                             a.action}
                          </div>
                          {a.nouveau_statut && (
                            <div style={{ fontSize: 11, color: '#27AE60', fontWeight: 600, marginBottom: 2 }}>
                              → {STATUTS[a.nouveau_statut]?.label || a.nouveau_statut}
                            </div>
                          )}
                          {a.details?.commentaire && (
                            <div style={{ fontSize: 11, color: '#888', fontStyle: 'italic', marginBottom: 2 }}>
                              "{a.details.commentaire}"
                            </div>
                          )}
                          <div style={{ fontSize: 10, color: '#aaa' }}>
                            {a.acteur ? a.acteur.prenom + ' ' + a.acteur.nom + ' · ' : ''}
                            {formatDateHeure(a.timestamp)}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* TRAÇABILITÉ */}
          <div style={s.card}>
            <div style={s.cardTitle}><IconeCadenas taille={15} /> Traçabilité</div>
            {[
              { label: 'Référence', value: demande.reference },
              { label: 'Créé le', value: formatDateHeure(demande.created_at) },
              { label: 'Dernière action', value: formatDateHeure(demande.updated_at) },
              { label: 'Nombre de fichiers', value: docs.length + ' fichier(s)' },
              { label: 'Taille totale', value: Math.round(docs.reduce((s, d) => s + (d.taille || 0), 0) / 1024) + ' Ko' },
              { label: 'Verrouillage', value: demande.gelee ? <><IconeCadenas taille={12} /> {'Dossier verrouillé — ' + formatDate(demande.gelee_at)}</> : <><IconeCadenas taille={12} /> Dossier en circulation</> },
            ].map((item, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: i < 5 ? '0.5px solid #f0f0f0' : 'none', fontSize: 12 }}>
                <span style={{ color: '#888' }}>{item.label}</span>
                <span style={{ fontWeight: 600, color: '#0D1B2A', textAlign: 'right', maxWidth: '60%' }}>{item.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const s = {
  page: { minHeight: '100vh', background: '#F0F2F5', fontFamily: 'Inter, system-ui, sans-serif' },
  header: { background: '#fff', borderBottom: '0.5px solid #e5e7eb', padding: '14px 28px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  backBtn: { padding: '8px 16px', borderRadius: 8, border: '0.5px solid #ddd', background: '#fff', cursor: 'pointer', fontSize: 13, fontFamily: 'inherit', marginRight: 8 },
  badge: { padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700 },
  circuit: { background: '#fff', borderBottom: '0.5px solid #e5e7eb', padding: '16px 28px' },
  circuitSteps: { display: 'flex', alignItems: 'center' },
  // Deux colonnes en bureau ; empilees sur mobile (surcharge au rendu)
  body: { display: 'grid', gridTemplateColumns: '1fr 320px', gap: 20, padding: '24px 28px', maxWidth: 1200, margin: '0 auto' },
  leftCol: { display: 'flex', flexDirection: 'column', gap: 16 },
  rightCol: { display: 'flex', flexDirection: 'column', gap: 16 },
  card: { background: '#fff', borderRadius: 14, border: '0.5px solid #e5e7eb', padding: '20px 24px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' },
  cardTitle: { fontSize: 14, fontWeight: 700, color: '#0D1B2A', marginBottom: 16 },
  infoGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
  infoItem: { background: '#F9FAFB', borderRadius: 10, padding: '12px 14px', border: '0.5px solid #e5e7eb' },
  infoLabel: { fontSize: 10, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6, fontWeight: 700 },
  datesGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 },
  dateItem: { background: '#F9FAFB', borderRadius: 10, padding: '10px 14px', border: '0.5px solid #e5e7eb' },
  textarea: { width: '100%', padding: '10px 12px', borderRadius: 8, border: '0.5px solid #ddd', fontSize: 12, fontFamily: 'inherit', outline: 'none', background: '#F9FAFB', boxSizing: 'border-box', resize: 'none', height: 70, marginBottom: 10 },
  btnApprouver: { width: '100%', padding: '12px', borderRadius: 10, border: 'none', background: '#27AE60', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', marginBottom: 8 },
  btnRejeter: { width: '100%', padding: '10px', borderRadius: 10, border: '1px solid #C0392B', background: 'transparent', color: '#C0392B', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', marginBottom: 8 },
  btnAnnuler: { flex: 1, padding: '10px', borderRadius: 8, border: '0.5px solid #ddd', background: '#fff', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' },
  errorMsg: { background: '#FDEDEC', color: '#96281B', padding: '8px 12px', borderRadius: 8, fontSize: 12, marginBottom: 10 },
};
